import {SplitBookingDetails} from 'components/Offer/types'
import {getSecondaryOffers} from 'components/SplitBookings/helpers'
import {HotelOfferEntity} from 'modules/sapiSearch/slice'
import {getTopOffers} from 'modules/sapiSearch/utils'

import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {getNightlyDisplayTotalPrice} from '@vio/core/src/room/business/transformSapiRoomsResponse/utils/price'
import {Room} from '@vio/core/src/room/types/room'
import {isOfferEligibleForDealFreeze} from '@vio/shared/src/dealFreeze/utils/OfferEligibility'

export enum DealFreezeOfferBoxScenario {
  None = 'none',
  NoVioFreezableRooms = 'noVioFreezableRooms',
  SecondaryVioNotFreezable = 'secondaryVioNotFreezable',
  SecondaryVioFreezable = 'secondaryVioFreezable',
  PrimaryVioNotFreezable = 'primaryVioNotFreezable',
  PrimaryVioFreezable = 'primaryVioFreezable'
}

interface VioTopOffer {
  offer: SearchOffer
  isPrimary: boolean
  isFreezable: boolean
}

// Mirrors the OfferBox rows, so offers a promoted bundle pushes out never count
export const getRenderedTopOffers = (
  offerEntity: HotelOfferEntity | undefined,
  splitBookingDetails: SplitBookingDetails
): SearchOffer[] => {
  const topOffers = getTopOffers(offerEntity)
  const {
    hasSplitBookingOffer,
    hasPrimarySplitBookingOffer,
    splitBookingPosition,
    offerPositionToRemove,
    splitBookingOffers
  } = splitBookingDetails
  const secondaryOffers = getSecondaryOffers({
    topOffers,
    hasSplitBookingOffer,
    splitBookingPosition,
    offerPositionToRemove
  })
  const bundleLeg = hasSplitBookingOffer
    ? splitBookingOffers[0]?.offer
    : undefined
  if (bundleLeg && hasPrimarySplitBookingOffer) {
    return [bundleLeg, ...secondaryOffers]
  }

  const rendered = [topOffers[0], ...secondaryOffers].filter(Boolean)
  if (bundleLeg) rendered.splice(splitBookingPosition, 0, bundleLeg)
  return rendered
}

// Split booking legs can carry the tag but the bundle itself cannot be frozen
export const getVioTopOffer = (
  topOffers: SearchOffer[],
  splitBookingOfferIds: string[] = []
): VioTopOffer | undefined => {
  const isFreezable = (offer: SearchOffer) =>
    isOfferEligibleForDealFreeze(offer.tags) &&
    !splitBookingOfferIds.includes(offer.id)
  const vioOffers = topOffers.filter(offer => isBofhOffer(offer))
  const offer = vioOffers.find(isFreezable) ?? vioOffers[0]
  if (!offer) return undefined

  return {
    offer,
    isPrimary: topOffers[0] === offer,
    isFreezable: isFreezable(offer)
  }
}

export const getDealFreezeOfferBoxScenario = (
  vioTopOffer: VioTopOffer | undefined,
  hasFreezableRooms: boolean
): DealFreezeOfferBoxScenario => {
  // Tag and banner both point at the rooms list, so a top offer the rooms
  // poll does not back up is no scenario at all
  if (!hasFreezableRooms) return DealFreezeOfferBoxScenario.None
  if (!vioTopOffer) return DealFreezeOfferBoxScenario.NoVioFreezableRooms

  if (vioTopOffer.isFreezable) {
    return vioTopOffer.isPrimary
      ? DealFreezeOfferBoxScenario.PrimaryVioFreezable
      : DealFreezeOfferBoxScenario.SecondaryVioFreezable
  }

  return vioTopOffer.isPrimary
    ? DealFreezeOfferBoxScenario.PrimaryVioNotFreezable
    : DealFreezeOfferBoxScenario.SecondaryVioNotFreezable
}

// Rooms sort by their cheapest offer, so the first freezable room may not hold the cheapest freezable offer
export const getCheapestFreezablePrice = (
  rooms: Room[],
  showTotalPrices: boolean
): number | undefined => {
  const priceOf = (prices: OfferPrice[] = []) =>
    showTotalPrices
      ? getTotalPrice(getDisplayPrice(prices))
      : getNightlyDisplayTotalPrice(prices)
  const prices = rooms
    .flatMap(room => room.offers)
    .filter(offer => isOfferEligibleForDealFreeze(offer.tags))
    .map(offer => priceOf(offer.prices))
    .filter((price): price is number => typeof price === 'number' && price > 0)

  return prices.length ? Math.min(...prices) : undefined
}
